<template>
  <div
    v-fade-on-scroll="$_onScroll"
    class="hero-block"
    :class="{ 'hero-block--scrolled': scrolled }">

    <div class="hero-block__wrap">
      <transition :name="animationName">
        <h1
          v-show="animateIn"
          class="hero-block__title">{{ title }}</h1>
      </transition>

      <transition :name="'subtitle-fade'">
        <span
          v-show="animateIn"
          v-portal="{ onClick: $_scrollToMainAction }"
          class="hero-block__subtitle">{{ subtitle }}</span>
      </transition>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import routesList from '@/router/routesList'

export default {
  data: () => ({
    scrolled: window.pageYOffset >= window.innerHeight * 1 / 3,
    animations: {
      left: 'hero-animation',
      right: 'hero-animation-reverse'
    }
  }),
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      required: true
    },
    animateIn: {
      type: Boolean,
      required: true
    },
    animationDirection: {
      type: String,
      required: true
    }
  },
  computed: {
    routesCount: {
      get () {
        return _.keys(routesList).length - 2
      }
    },
    currentRoutePosition: {
      get () {
        return this.$route.meta.position - 1
      }
    },
    animationName: {
      get () {
        return this.animations[this.animationDirection]
      }
    },
    topTranslation: {
      get () {
        return Math.round(this.windowScrollPosition * this.parallax.ratio - this.parallax.fixture)
      }
    }
  },
  methods: {
    $_onScroll () {
      this.scrolled = window.pageYOffset >= window.innerHeight * 1 / 3
    },
    $_scrollToMainAction () {
      const mainAction = document.querySelector('.content-wrapper')
      if (mainAction === null) return

      mainAction.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start'
      })
    }
  },
  directives: {
    'fade-on-scroll': {
      bind (el, binding) {
        window.addEventListener('scroll', binding.value)
      },
      unbind (el, binding) {
        window.removeEventListener('scroll', binding.value)
      }
    },
    portal: {
      bind (el, binding, vnode) {
        vnode.context.$data._portalGhost = document.createElement('div')
        vnode.context.$data._portalGhost.addEventListener('click', binding.value.onClick)
        document.body.appendChild(vnode.context.$data._portalGhost)

        const updateGhost = () => {
          const display = vnode.context.$data.scrolled ? 'none' : 'block'
          const zIndex = vnode.context.$data.scrolled ? -1 : 200
          const { left, top } = el.getBoundingClientRect()
          vnode.context.$data._portalGhost.innerHTML = el.innerHTML
          vnode.context.$data._portalGhost.setAttribute('style', `z-index: ${zIndex}; display: ${display}; left: ${left + window.pageXOffset}px; top: ${top + window.pageYOffset}px;`)
          vnode.context.$data._portalGhost.setAttribute('class', el.getAttribute('class') + ' hero-block__ghost')
        }

        const cycle = () => {
          updateGhost()
          window.requestAnimationFrame(cycle)
        }

        cycle()
      },
      unbind (el, binding, vnode) {
        document.body.removeChild(vnode.context.$data._portalGhost)
      }
    }
  }
}
</script>

<style lang="scss">

@import '../assets/scss/breakpoint.scss';

$white: #f2f2f2;
$marginLeftRight: 7.5%;
$marginLeftRightDesktop: 11.5%;

.hero-block {
  position: relative;
  min-width: 100vw;
  min-height: 100vh;
  transition: all .5s;
}

.hero-block__subtitle.hero-block__ghost {
  position: absolute;
  opacity: 0;
  margin: 0;
  cursor: pointer;
  max-width: calc(100vw - #{$marginLeftRight * 2} - 50px);

  @include media(">=large"){
    max-width: calc(100vw - #{$marginLeftRightDesktop});
  }
}

.hero-block--scrolled {
  opacity: 0;
}

.hero-block__navigation-position {
  writing-mode: vertical-lr;
  color: white;
  position: fixed;
  text-align: center;
  padding: 3.25%;
  box-sizing: border-box;
  display: none;
  top: 50vh;
  transform: translateY(-50%);
  left: 0;

  span {
    transform: rotate(180deg);
    font-size: 1.6rem;
    @include media(">=xlarge"){
      font-size: 1.8rem;
    }
  }

  @include media(">=large"){
    display: flex;
    align-items: center;
  }
}

.hero-block__navigation-position__line {
  width: 1px;
  height: 100px;
  background-color: white;
  display: inline-block;
  margin: 2.5rem 0;
}

.hero-block__wrap {
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  width: 80%;
  /* height: 100vh; */
  top: 48%;
  transform: translateX(0) translateY(-50%);
  @include media(">=large"){
    width: 100%;
    top: 50%;
  }
}

.hero-block__title {
  display: block !important;
  margin: 0 $marginLeftRight;
  color: $white;
  font-size: 4.25rem;
  line-height: 1;

  @include media(">=large"){
    font-size: 9rem;
    margin: 0 $marginLeftRightDesktop;
    margin-bottom: 2.5rem;
  }

  @include media(">=xlarge"){
    font-size: 13rem;
  }
}

.hero-block__subtitle {
  display: block !important;
  margin: 1rem calc(#{$marginLeftRight} + 5px) 0;
  color: #90D5DB;
  font-size: 1.8rem;
  @include media(">=large"){
    margin: .5rem calc(#{$marginLeftRightDesktop} + 10px) 0;
  }
  @include media(">=xlarge"){
    font-size: 2.4rem;
  }
}

/* @group Transition */
$distance: 70vw;

.hero-animation-enter-active, .hero-animation-active {
  transition: all 1.7s .3s;
}

.hero-animation-reverse-enter-active, .hero-animation-reverse-active {
  transition: all 1.7s .3s;
}

.hero-animation-enter {
  transform: translateX(#{$distance});
}

.hero-animation-leave {
  transform: translateX(0);
}

.hero-animation-leave-to {
  transform: translateX(-#{$distance});
}

.hero-animation-enter-to, .hero-animation-reverse-enter-to {
  transform: translateX(0);
}

.hero-animation-reverse-enter {
  transform: translateX(-#{$distance});
}

.hero-animation-reverse-leave {
  transform: translateX(0);
}

.hero-animation-reverse-leave-to {
  transform: translateX(#{$distance});
}

.subtitle-fade-enter {
  opacity: 0;
}

.subtitle-fade-enter-to {
  opacity: 1;
}

.subtitle-fade-leave {
  opacity: 1;
}

.subtitle-fade-leave-to {
  opacity: 0;
}

.subtitle-fade-enter-active, .subtitle-fade-leave-active {
  transition: opacity .3s 2s;
}

/* @end */
</style>
